import { useEffect, useState } from "react";
import Lottie from "lottie-react";

interface LottieFromUrlProps {
  url?: string | null;
  className?: string;
  loop?: boolean;
  autoplay?: boolean;
  /** Optional local JSON to render while the remote one is loading / on failure */
  fallback?: object | null;
}

/**
 * Renders a Lottie animation from any URL.
 *
 * Why not <YetiCharacter>?
 *   YetiCharacter only treats the URL as a Lottie if it contains ".json"/"lottie",
 *   and otherwise renders it as an <img>. Most CDN Lottie URLs don't carry those
 *   substrings (e.g. `https://assets.prod.../abc123`), so the <img> request fails
 *   silently and the yetiWaving fallback shows instead.
 *
 * This component:
 *   1. Always fetches the URL as text
 *   2. Tries to parse it as JSON → render with lottie-react
 *   3. If parsing fails (binary, .lottie zip, image), renders as <img>
 *   4. If everything fails and a `fallback` Lottie was provided, renders that
 */
function LottieFromUrl({
  url,
  className = "",
  loop = true,
  autoplay = true,
  fallback = null,
}: LottieFromUrlProps) {
  const [data, setData] = useState<object | null>(null);
  const [imageMode, setImageMode] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setData(null);
    setImageMode(false);
    setFailed(false);

    if (!url) {
      setFailed(true);
      return;
    }

    let cancelled = false;

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (cancelled) return;
        try {
          const json = JSON.parse(text);
          if (json && typeof json === "object") {
            setData(json);
            return;
          }
        } catch {
          // not JSON — try as image
        }
        setImageMode(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => { cancelled = true; };
  }, [url]);

  // Successfully fetched JSON → render with lottie-react
  if (data) {
    return (
      <Lottie animationData={data} loop={loop} autoplay={autoplay} className={className} />
    );
  }

  // Response wasn't JSON — try rendering as an image
  if (imageMode && url) {
    return (
      <img
        src={url}
        alt=""
        className={className}
        onError={() => { setImageMode(false); setFailed(true); }}
      />
    );
  }

  // Fully failed — render fallback Lottie if provided, else nothing
  if (failed && fallback) {
    return (
      <Lottie animationData={fallback} loop={loop} autoplay={autoplay} className={className} />
    );
  }

  // Still loading — keep space reserved
  return <div className={className} aria-hidden="true" />;
}

export default LottieFromUrl;
